import { forwardRef, useState } from "react";
import { keyframes } from "@emotion/react";
import styled from "@emotion/styled";
import { Collapsible, CollapsibleTrigger } from "@radix-ui/react-collapsible";
import { Text } from "styles/utility-components";

import { FeatureType } from "components/paywall/paywallUtils";
import { generateSavedViewUrl } from "components/saved_searches/utils";
import BetaIcon, { BetaFeatureType } from "components/ui/beta_icon/BetaIcon";
import Button, {
  AnchorButtonProps,
  ButtonButtonProps,
  CombinedButtonProps,
} from "components/ui/button/Button";
import ButtonSplit, { ButtonSplitProps } from "components/ui/button_split/ButtonSplit";
import { DropdownMenu, DropdownMenuItem } from "components/ui/dropdown_menu";
import { SidebarMenu, SidebarWrapper, useOptionalSidebar } from "components/ui/sidebar/Sidebar";
import EllipsisTooltip from "components/ui/tooltip/EllipsisTooltip";
import TooltipComponent, { TooltipComponentProps } from "components/ui/tooltip/Tooltip";
import { useSubscription } from "lib/hooks/subscription";
import { UIcon } from "lib/icons/untitled_ui/UIcon";
import { OpenPaywallModalOptions, usePaywall } from "lib/providers/Paywall";
import { routes } from "lib/routes";
import { toAndSearch } from "lib/spaNavigation";
import { SavedViewDataType } from "lib/types/graphQLEnums";
import { DataTypeName } from "lib/types/models";

export const isRouteActive = (testPath: string | undefined, test?: RegExp): boolean => {
  // Test against the regex if provided
  const currentPathAndQuery = window.location.pathname + window.location.search;
  if (test) {
    return test.test(currentPathAndQuery);
  }

  // If no path provided, cannot be active since it's a non-route item
  if (testPath === undefined) {
    return false;
  }

  const currentPath = window.location.pathname;
  const testPathNoQuery = testPath.split("?")[0]; // Remove query parameters
  // Exact match
  if (currentPath === testPathNoQuery) {
    return true;
  }

  // Sub-route match e.g. /notices matches /notices/analysis
  if (testPathNoQuery === "/") {
    return false; // Root path should only match exactly
  }
  const trailingPath = testPathNoQuery.endsWith("/") ? testPathNoQuery : `${testPathNoQuery}/`;
  return currentPath.startsWith(trailingPath);
};

export const generateRegexForSavedViewUrl = (viewId: string, dataType: `${SavedViewDataType}`) => {
  const url = generateSavedViewUrl(viewId, dataType);
  return new RegExp(
    // Match either ? or / after path, then any other query params before current_view_id
    `^${url.replace("?", "(?=[/?]).*[?&]")}`,
  );
};

type NavItemBaseProps = {
  label: string;
  iconOnly?: boolean;
  tooltipProps?: Omit<TooltipComponentProps, "children" | "content">;
  activeRegex?: RegExp;
  isGuestUser?: boolean;
  tag?: {
    type: "beta" | "notificationCount";
    betaFeature?: BetaFeatureType;
    count?: number;
  };
  paywall?: {
    dataType: DataTypeName;
    featureType: FeatureType;
    options?: OpenPaywallModalOptions;
  };
};
// Anchor variant that also accepts a TanStack Router `to`/`search` (SPA navigation)
// alongside the plain `href` (full reload) used for external links.
type NavItemAnchorProps = Omit<AnchorButtonProps, "children" | "href" | "to"> & {
  href?: string;
  to?: string;
  search?: Record<string, unknown>;
};

type NavItemBaseInternalProps = NavItemBaseProps & {
  passedRef?: React.ForwardedRef<HTMLButtonElement>; // Attached to <Button />. Could be Button or Anchor but hard to type better
};

const NavItemBase = ({
  label,
  iconOnly,
  tooltipProps,
  activeRegex,
  isGuestUser,
  tag,
  paywall,
  onClick,
  passedRef,
  ...buttonProps
}: (NavItemAnchorProps | Omit<ButtonButtonProps, "children">) & NavItemBaseInternalProps) => {
  const { openPaywallModal } = usePaywall();
  const subscription = useSubscription();

  const linkDestination =
    buttonProps?.as === "a"
      ? (buttonProps as NavItemAnchorProps).to ?? (buttonProps as NavItemAnchorProps).href
      : undefined;
  const isActive = isRouteActive(linkDestination, activeRegex);
  const isDisabled = (buttonProps.disabled || isGuestUser) && !isActive; // If on route currently, don't disable
  const isLocked = paywall && !isGuestUser ? !subscription.hasDataTypes(paywall.dataType) : false;

  const onClickWrapper =
    isLocked && paywall
      ? (e: React.MouseEvent<HTMLButtonElement>) => {
          e.preventDefault();
          openPaywallModal(paywall.featureType, paywall.options);
        }
      : onClick;

  if (iconOnly) {
    return (
      <TooltipComponent content={label} {...tooltipProps}>
        <NavItemInternalButton
          aria-label={label}
          buttonStyle="inverse"
          buttonType="primary"
          data-route-active={isActive}
          disabled={isDisabled}
          onClick={onClickWrapper as React.MouseEventHandler<HTMLButtonElement>}
          ref={passedRef as React.ForwardedRef<HTMLButtonElement>}
          {...(buttonProps as CombinedButtonProps)}
        />
      </TooltipComponent>
    );
  }

  return (
    <EllipsisTooltip fullText={label} tooltipProps={{ side: "right", ...tooltipProps }}>
      {(ref) => (
        <NavItemInternalButton
          aria-label={label}
          buttonStyle="inverse"
          buttonType="primary"
          data-route-active={isActive}
          disabled={isDisabled}
          justifyContent="start"
          onClick={onClickWrapper as React.MouseEventHandler<HTMLButtonElement>}
          ref={passedRef}
          {...(buttonProps as CombinedButtonProps)}
        >
          <Text ref={ref} ellipsis color="inherit">
            {label}
          </Text>
          {isLocked && <UIcon icon="lock01" />}
          {tag && (
            <NavItemEndContainer>
              {tag?.type === "beta" && <BetaIcon betaFeature={tag.betaFeature} />}
            </NavItemEndContainer>
          )}
        </NavItemInternalButton>
      )}
    </EllipsisTooltip>
  );
};

// Internal app paths navigate via TanStack Router (`to`/`search`) for instant SPA
// transitions; external URLs (e.g. the help centre) fall back to a plain `href`.
const navLinkDestination = (
  href: string,
): { to: string; search?: Record<string, unknown> } | { href: string } => {
  // Any URL with a scheme (https:, mailto:, tel:, …) or protocol-relative `//` is
  // external — only path-relative hrefs are SPA-navigable.
  if (/^([a-z][a-z0-9+.-]*:|\/\/)/i.test(href)) {
    return { href };
  }
  // Reuse toAndSearch so the search object is parsed with the same qsSettings the
  // router uses (consistent shape with what the destination route reads).
  return toAndSearch(href);
};

type NavItemLinkProps = Omit<AnchorButtonProps, "as" | "children"> & NavItemBaseProps;
export const NavItemLink = forwardRef<HTMLAnchorElement, NavItemLinkProps>(
  ({ href, ...props }, ref) => (
    <NavItemInternalList data-icon-only={props.iconOnly ? "true" : undefined}>
      <NavItemBase
        as="a"
        passedRef={ref as React.ForwardedRef<HTMLButtonElement>}
        {...props}
        {...navLinkDestination(href)}
      />
    </NavItemInternalList>
  ),
);
NavItemLink.displayName = "NavItemLink";

type NavItemButtonProps = Omit<ButtonButtonProps, "as" | "children"> & NavItemBaseProps;
export const NavItemButton = forwardRef<HTMLButtonElement, NavItemButtonProps>((props, ref) => (
  <NavItemInternalList data-icon-only={props.iconOnly ? "true" : undefined}>
    <NavItemBase as="button" passedRef={ref} {...props} />
  </NavItemInternalList>
));
NavItemButton.displayName = "NavItemButton";

const dropdownTriggerActiveProps = (hrefs: string[]) => ({
  // NavItemInternalButton only styles data-route-active (NavItemBase's own computed
  // isActive is always false here since this is a plain button, not a link) — not
  // data-active, which was a pre-existing dead prop.
  "data-route-active": hrefs.some((href) => isRouteActive(href)),
});

const dropdownItemActiveProps = (href: string, isGuestUser: boolean, excludeRoute?: string) => {
  const isActive = isRouteActive(href) && (excludeRoute ? !isRouteActive(excludeRoute) : true);
  return { to: href, "data-active": isActive, disabled: isGuestUser && !isActive };
};

type NavDropdownProps = {
  isGuestUser: boolean;
  side?: "top" | "right";
};

export const ListsNavDropdown = ({ isGuestUser, side = "top" }: NavDropdownProps) => (
  <DropdownMenu
    width="fit-content"
    side={side}
    modal={false}
    trigger={
      <NavItemButton
        icon="list"
        label="Lists"
        {...dropdownTriggerActiveProps([routes.buyerLists, routes.noticeLists])}
      />
    }
  >
    <DropdownMenuItem as="a" {...dropdownItemActiveProps(routes.buyerLists, isGuestUser)}>
      Buyer lists
    </DropdownMenuItem>
    <DropdownMenuItem as="a" {...dropdownItemActiveProps(routes.noticeLists, isGuestUser)}>
      Notice lists
    </DropdownMenuItem>
  </DropdownMenu>
);

export const ReportsNavDropdown = ({ isGuestUser, side = "top" }: NavDropdownProps) => (
  <DropdownMenu
    width="fit-content"
    side={side}
    modal={false}
    trigger={
      <NavItemButton
        icon="presentationChart02"
        label="Reports"
        {...dropdownTriggerActiveProps(["/reports", routes.industryReports])}
      />
    }
  >
    <DropdownMenuItem
      as="a"
      // Custom briefings live at /reports/<uuid> but the industry reports list is
      // annoyingly also under /reports (at /reports/industry), so matching "/reports"
      // alone would wrongly highlight this item there too — exclude it explicitly.
      {...dropdownItemActiveProps("/reports", isGuestUser, routes.industryReports)}
      to={routes.customBriefings}
    >
      Custom briefings
    </DropdownMenuItem>
    <DropdownMenuItem as="a" {...dropdownItemActiveProps(routes.industryReports, isGuestUser)}>
      Industry reports
    </DropdownMenuItem>
  </DropdownMenu>
);

type NavItemLinkSplitProps = NavItemLinkProps & {
  children: React.ReactNode;
  secondaryType: ButtonSplitProps["secondaryType"];
};
export const NavItemLinkSplit = ({ secondaryType, children, ...props }: NavItemLinkSplitProps) => (
  <ButtonSplitList as="li" secondaryType={secondaryType}>
    <NavItemBase as="a" {...props} />
    {children}
  </ButtonSplitList>
);

const NavItemInternalButton = styled(Button)(({ theme }) => ({
  padding: "8px",
  width: "100%",
  minWidth: 0, // So it overflows correctly

  // Inactive items sit at secondary and come forward to default text on hover/press. Keyed off the
  // attribute (not the bare selector) so it outweighs Button's own colour rule; skips disabled.
  '&[data-route-active=false]:not([aria-disabled="true"])': {
    color: theme.colors.sysTextSecondary,
  },
  '&[data-route-active=false]:not([aria-disabled="true"]):hover, &[data-route-active=false]:not([aria-disabled="true"]):active':
    {
      color: theme.colors.sysTextDefault,
    },

  "&[data-route-active=true]": {
    backgroundColor: theme.colors.sysPrimarySubtle,
  },

  // Tighter row density for nav items in the content area (see Content.tsx's
  // data-nav-tighten): Text's default line-height is 157% of 14px (~22px), so
  // 5px top/bottom padding + ~22px line-height hits the target 32px row height.
  '[data-nav-tighten="true"] &': {
    paddingTop: 5,
    paddingBottom: 5,
  },
}));

const NavItemInternalList = styled.li({
  listStyle: "none",
  position: "relative",
  "&:not([data-icon-only=true])": {
    width: `calc(var(--sidebar-width) - 32px - var(--scrollbar-width))`, // 32px is SidebarGroup padding
  },
  // In the mobile panel --sidebar-width is 100% and already resolves against SidebarGroup's padded
  // box, so subtracting the padding again would leave the row background short of the 16px gutter.
  '[data-mobile-nav="true"] &:not([data-icon-only=true])': {
    width: "100%",
  },
});

const ButtonSplitList = styled(ButtonSplit)({
  listStyle: "none",
  position: "relative",
  width: `calc(var(--sidebar-width) - 32px - var(--scrollbar-width))`, // 32px is SidebarGroup padding

  // Same percentage-vs-absolute issue as NavItemInternalList above.
  '[data-mobile-nav="true"] &': {
    width: "100%",
  },

  // Pinned stays visible in the icon-collapsed rail (see SidebarMenuIconCollapsible), but
  // hide the secondary "..." action button — it's never been rendered icon-only before and
  // the icon-collapsed state is transient anyway (iconHover="overlay" expands on hover).
  [`${SidebarWrapper}[data-collapsible=icon] &`]: {
    "& > *:last-child": {
      display: "none",
    },
  },
});

const NavItemEndContainer = styled.div({
  marginLeft: "auto",
});

const SidebarCollapsibleTriggerInternal = styled(NavItemInternalButton)({
  overflow: "hidden",
  paddingLeft: 0,
});

const SidebarCollapsibleBar = styled.div(({ theme }) => ({
  backgroundColor: theme.colors.grey100,
  width: 0,
  height: 1,
  flexShrink: 0,
  transition: "width 100ms ease-in-out",
  [`${SidebarWrapper}[data-collapsible=icon] &`]: {
    width: "32px",
    transitionDelay: "100ms",
  },
}));

export const SidebarMenuCollapsibleChevron = styled(UIcon)({
  transition:
    "transform 200ms ease-in-out, opacity 50ms ease-in-out 150ms, visibility 50ms ease-in-out 150ms",
  [`${SidebarCollapsibleTriggerInternal}[data-state=open] &`]: {
    transform: "rotate(180deg)",
  },
  [`${SidebarWrapper}[data-collapsible=icon] &`]: {
    opacity: 0,
    visibility: "hidden",
    transitionDelay: "0ms",
  },
});

export const SidebarMenuCollapsibleText = styled(Text)({
  flexGrow: 1,
  transition: "opacity 80ms ease-in-out, visibility 80ms ease-in-out",
  transitionDelay: "120ms",
  [`${SidebarWrapper}[data-collapsible=icon] &`]: {
    opacity: 0,
    visibility: "hidden",
    transitionDelay: "0ms",
  },
});

type SidebarCollapsibleTriggerProps = Omit<ButtonButtonProps, "as"> & {
  children?: string;
};

// Wraps a nav group's Radix Collapsible. On mobile the group has no trigger (its heading is a
// plain label — see SidebarCollapsibleTrigger), so force it open; otherwise a group the user
// collapsed on a wider layout would be stuck closed with no way to reopen it after resizing down.
export const SidebarCollapsibleGroup = ({ children }: { children: React.ReactNode }) => {
  const isMobile = useOptionalSidebar()?.isMobile ?? false;
  const [open, setOpen] = useState(true);
  return (
    <Collapsible open={isMobile || open} onOpenChange={setOpen}>
      {children}
    </Collapsible>
  );
};

export const SidebarCollapsibleTrigger = ({
  children,
  ...buttonProps
}: SidebarCollapsibleTriggerProps) => {
  // No provider (e.g. a nav group rendered standalone) behaves as non-mobile.
  const isMobile = useOptionalSidebar()?.isMobile ?? false;

  // Mobile: groups stay expanded (the panel scrolls), so the heading is a plain label — no
  // trigger and no chevron. SidebarCollapsibleGroup force-opens the group so it can't get stuck
  // closed if the user had collapsed it on a wider layout.
  if (isMobile) {
    return (
      <SidebarCollapsibleHeading>
        <SidebarMenuCollapsibleText
          description
          color="sysTextSecondary"
          fontWeight="500"
          textTransform="uppercase"
        >
          {children}
        </SidebarMenuCollapsibleText>
      </SidebarCollapsibleHeading>
    );
  }

  return (
    <CollapsibleTrigger asChild>
      <SidebarCollapsibleTriggerInternal
        data-ignore-state-styles
        buttonStyle="inverse"
        buttonType="primary"
        justifyContent="start"
        {...(buttonProps as CombinedButtonProps)}
      >
        <SidebarCollapsibleBar />
        <SidebarMenuCollapsibleText
          description
          color="sysTextSecondary"
          fontWeight="500"
          textTransform="uppercase"
        >
          {children}
        </SidebarMenuCollapsibleText>
        <SidebarMenuCollapsibleChevron icon="chevronDown" color="sysTextPlaceholder" size={12} />
      </SidebarCollapsibleTriggerInternal>
    </CollapsibleTrigger>
  );
};

// Non-interactive group heading for the mobile panel: 32px row, 8px inset, per the design.
const SidebarCollapsibleHeading = styled.div({
  display: "flex",
  alignItems: "center",
  height: 32,
  padding: "0 8px",
});

const slideDownCollapsible = keyframes({
  from: {
    height: "0",
  },
  to: {
    height: "var(--radix-collapsible-content-height)",
  },
});

const slideUpCollapsible = keyframes({
  from: {
    height: "var(--radix-collapsible-content-height)",
  },
  to: {
    height: "0",
  },
});

// For use with radix's CollapsibleTrigger with asChild prop
export const SidebarMenuCollapsible = styled(SidebarMenu)({
  "&[data-state=open]": {
    animation: `${slideDownCollapsible} 200ms ease-out`,
  },
  "&[data-state=closed]": {
    animation: `${slideUpCollapsible} 200ms ease-out`,
  },
});

// Wrap radix's CollapsibleTrigger with this. You still need SidebarMenuCollapsible
// Can't use --radix-collapsible-content-height since that is only updated on trigger
// CSS interpolate-size is the better option but is currently unsupported by Firefox
// so going for an alternative approach instead
//
// Pinned (the only consumer) is not force-hidden when the sidebar goes icon-only — it's
// hidden only if the user had already collapsed the Pinned group itself, where Radix's
// open state drives visibility via SidebarMenuCollapsible's height animation, same as it
// does when the sidebar is fully expanded.
export const SidebarMenuIconCollapsible = styled.div({
  display: "grid",
  gridTemplateRows: "1fr",
  transition: "grid-template-rows 200ms ease-out, visibility 200ms",
  visibility: "visible",
  // We want to hide any open dropdown menu when the sidebar is opening/closing & when it is shut
  // Can't use display: none since that has animation issues
  "& [data-radix-popper-content-wrapper]": {
    transition: "visibility 1ms 200ms, opacity 200ms ease-out 200ms",
    visibility: "visible",
    opacity: 1,
  },
  [`${SidebarWrapper}[data-collapsible=icon] & [data-radix-popper-content-wrapper]`]: {
    visibility: "hidden",
    opacity: 0,
    transitionDelay: "0ms",
  },
});
